const BASE_URL = 'https://vivotal-backend.vercel.app/api/v1';

/**
 * @description Fetches all providers from the API.
 * @param token Authorization token for API access.
 * @returns A promise resolving to the list of providers.
 */
export const fetchAllProviders = async (token: string, limit: number = 10, page: number = 1) => {
    try {
        const response = await fetch(`${BASE_URL}/providers?limit=${limit}&page=${page}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        return data;
    } catch (error: any) {
        console.error("Failed to load providers:", error.message);
        throw error;
    }
}

/**
 * @description Fetches details of a specific provider from the API.
 * @param token Authorization token for API access.
 * @param providerId The ID of the provider to fetch details for.
 * @returns A promise resolving to the provider details.
 */
export const fetchProviderDetails = async (token: string, providerId: string) => {
    try {
        const response = await fetch(`${BASE_URL}/providers/${providerId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const data = await response.json();
        return data
    } catch (error) {
        console.error("Failed to load provider details:", error);
    }
}

/**
 * @description Fetches all no-show appointments from the API.
 * @param token Authorization token for API access.
 * @param providerId Optional provider ID to filter no-show appointments.
 * @returns A promise resolving to the list of no-show appointments.
 */
export const fetchNoShowAppointments = async (token: string, providerId?: string) => {
    try {
        const url = providerId 
            ? `${BASE_URL}/no-show/list?provider_id=${providerId}`
            : `${BASE_URL}/no-show/list`;
        
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Failed to load no-show appointments:", error);
        throw error;
    }
}

/**
 * @description Fetches a specific no-show appointment by appointment ID.
 * @param token Authorization token for API access.
 * @param appointmentId The appointment ID to fetch.
 * @returns A promise resolving to the no-show appointment details.
 */
export const fetchNoShowByAppointmentId = async (token: string, appointmentId: string) => {
    try {
        const response = await fetch(`${BASE_URL}/no-show/list`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const data = await response.json();
        
        // Find the specific appointment from the list
        const noShowData = data?.data || [];
        const record = noShowData.find((item: any) => item.appointment_id === appointmentId);
        
        return record;
    } catch (error) {
        console.error("Failed to load no-show appointment details:", error);
        throw error;
    }
}

/**
 * @description Fetches follow-up messages for no-show appointments.
 * @param token Authorization token for API access.
 * @param patientId Optional patient ID to filter messages.
 * @param providerId Optional provider ID to filter messages.
 * @param includePending Whether to include messages not yet ready (default: false).
 * @returns A promise resolving to the follow-up messages grouped by thread.
 */
export const fetchFollowUpMessages = async (
    token: string, 
    patientId?: string, 
    providerId?: string, 
    includePending: boolean = false
) => {
    try {
        const params = new URLSearchParams();
        if (patientId) params.append('patient_id', patientId);
        if (providerId) params.append('provider_id', providerId);
        if (includePending) params.append('include_pending', 'true');
        
        const url = `${BASE_URL}/no-show/messages${params.toString() ? '?' + params.toString() : ''}`;
        
        console.log('[FollowUp API] Fetching messages from:', url);
        console.log('[FollowUp API] Parameters:', { patientId, providerId, includePending });
        
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        console.log('[FollowUp API] Response status:', response.status);
        
        // If 404, throw specific error
        if (response.status === 404) {
            const errorText = await response.text();
            console.error('[FollowUp API] 404 Error:', errorText);
            const error: any = new Error(`Endpoint not found (404): ${errorText}`);
            error.response = { status: 404 };
            throw error;
        }
        
        // If other error status
        if (!response.ok) {
            let errorText = '';
            let errorData = null;
            
            try {
                errorData = await response.json();
                errorText = errorData?.data?.errorMessage || errorData?.message || JSON.stringify(errorData);
                console.error('[FollowUp API] Error response data:', errorData);
            } catch {
                // If JSON parsing fails, try to get text response
                errorText = await response.text();
                console.error('[FollowUp API] Error response text:', errorText);
            }
            
            const error: any = new Error(`HTTP ${response.status}: ${errorText}`);
            error.response = { status: response.status, data: errorData };
            throw error;
        }
        
        const data = await response.json();
        console.log('[FollowUp API] Response data:', data);
        return data;
    } catch (error: any) {
        console.error("[FollowUp API] Failed to load follow-up messages:", error);
        // Re-throw to allow component to handle it
        throw error;
    }
}

/**
 * @description Fetches a specific follow-up message by ID.
 * @param token Authorization token for API access.
 * @param messageId The ID of the message to fetch.
 * @returns A promise resolving to the follow-up message details.
 */
export const fetchFollowUpMessageById = async (token: string, messageId: string) => {
    try {
        const response = await fetch(`${BASE_URL}/no-show/messages/${messageId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Failed to load follow-up message details:", error);
        throw error;
    }
}